import { getVariableValue } from "@/shared/utils/resolve-tokens";
import type { OptionalConfig } from "./types";

/**
 * Per-version map of sender choices for simple-mode optional entities, keyed by
 * entity id (`true` = include). Persisted into the version `data` by the share
 * dialog's OptionalContentCards and consulted here at render/compile time.
 */
export const OPTIONAL_CONTENT_KEY = "_optionalContent";

interface OptionalNode {
  id?: string;
  _id?: string;
  settings?: { optionalConfig?: OptionalConfig };
  children?: OptionalNode[];
}

/**
 * Evaluates a conditional optional rule against the resolved variable values.
 * Returns whether the entity should be SHOWN.
 */
/**
 * The author types the comparison value by hand while the sender picks or
 * types theirs — `"Enterprise "` against `"enterprise"` is the same answer.
 * Whitespace and case never decide whether a slide shows.
 */
const normalizeConditionValue = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const evaluateOptionalCondition = (
  config: Extract<OptionalConfig, { mode: "conditional" }>,
  values: Record<string, unknown>,
): boolean => {
  const actual = getVariableValue(values, config.variable);
  const expected = normalizeConditionValue(config.value);
  // A multiselect stores a list: the rule holds when any chosen option matches.
  const matches = Array.isArray(actual)
    ? actual.some((item) => normalizeConditionValue(item) === expected)
    : normalizeConditionValue(actual) === expected;
  const condition = config.operator === "equals" ? matches : !matches;
  return config.then === "show" ? condition : !condition;
};

/**
 * Resolves whether a single optional entity should be included in the rendered
 * document. Simple mode consults the sender's choice (defaulting to included);
 * conditional mode evaluates the rule against the version `data`.
 */
export const isOptionalEntityIncluded = (
  config: OptionalConfig,
  id: string | undefined,
  data: Record<string, unknown>,
): boolean => {
  if (config.mode === "simple") {
    const choices = (data?.[OPTIONAL_CONTENT_KEY] ?? {}) as Record<
      string,
      boolean
    >;
    return id ? (choices[id] ?? true) : true;
  }
  return evaluateOptionalCondition(config, data);
};

const pruneNodes = <T extends OptionalNode>(
  nodes: T[],
  data: Record<string, unknown>,
  settle = false,
): T[] =>
  nodes
    .filter((node) => {
      const config = node?.settings?.optionalConfig;
      if (!config) return true;
      return isOptionalEntityIncluded(config, node.id ?? node._id, data);
    })
    .map((node) => {
      let next = node;
      if (settle && next?.settings?.optionalConfig) {
        const { optionalConfig: _decided, ...settings } = next.settings;
        next = { ...next, settings };
      }
      return Array.isArray(next?.children)
        ? { ...next, children: pruneNodes(next.children, data, settle) }
        : next;
    });

/**
 * Drops optional entities that resolve to excluded/hidden from a slides array,
 * recursing through every nested layer (combo → set → group → element). Pure —
 * returns a new array and never mutates the input.
 */
export const pruneOptionalSlides = <T extends OptionalNode>(
  slides: T[],
  data: Record<string, unknown>,
): T[] => (Array.isArray(slides) ? pruneNodes(slides, data) : slides);

/**
 * Story-level wrapper: returns a copy of the story with optional-excluded
 * entities removed from `slides`. No-op when the story has no slides array.
 */
export const pruneOptionalEntities = <
  T extends { slides?: OptionalNode[] } | null | undefined,
>(
  story: T,
  data: Record<string, unknown>,
): T => {
  if (!story || !Array.isArray(story.slides)) return story;
  return { ...story, slides: pruneNodes(story.slides, data) };
};

/**
 * Editor preview HTML (Present → Document preview / Open in a new tab) is
 * built here and never reaches the server that fills `window.__SD_DATA__`, so
 * the bundle re-runs its own prune against `{}` and hides every conditional
 * entity whose rule needs a variable value. Decide here against the version
 * data, then drop `optionalConfig` from the survivors so the bundle's second
 * pass keeps them — without putting the condition variables in the public
 * preview HTML.
 */
export const pruneOptionalEntitiesForPreview = <
  T extends { slides?: OptionalNode[] } | null | undefined,
>(
  story: T,
  data: Record<string, unknown>,
): T => {
  if (!story || !Array.isArray(story.slides)) return story;
  return { ...story, slides: pruneNodes(story.slides, data, true) };
};
